import { useEffect, useRef, useState } from 'react';
import { FiAlertCircle, FiArrowLeft, FiCheck, FiImage, FiMail, FiX } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import renderMarkdown from '../renderMarkdown';
import { Bars } from './AdminCharts';
import { PLANS, STAGES, ago, api, day, errorText, identity, number } from './adminFormat';

// The 12 months ending this one, zero-filled.
function lastMonths(rows, count = 12) {
  const byMonth = Object.fromEntries(rows.map((row) => [row.month, row]));
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (count - 1 - i), 1);
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return byMonth[month] || { month, conversations: 0, questions: 0 };
  });
}

const FIELDS = { status: 'Stage', organization: 'Organization', tags: 'Tags', follow_up_at: 'Follow-up' };
const shown = (key, value) => (key === 'status' ? STAGES[value] || value
  : Array.isArray(value) ? value.join(', ') || 'none' : value || 'none');

export function describeAudit(entry) {
  if (entry.action === 'plan') return `Plan ${entry.before?.plan || '?'} → ${entry.after?.plan}`;
  if (entry.action === 'note') return 'Added a note';
  if (entry.action === 'view_conversation') return `Opened the conversation “${entry.after?.title || 'untitled'}”`;
  if (entry.action === 'subscription') return entry.after?.news ? 'Subscribed again to news emails (they asked)' : 'Unsubscribed from news emails (they asked)';
  if (entry.action === 'email_message' || entry.action === 'whatsapp_message') {
    const what = entry.action === 'email_message' ? `Emailed “${entry.after?.subject || ''}”`
      : entry.after?.subject ? `Sent the WhatsApp ${entry.after.subject.toLowerCase()}` : 'Sent a WhatsApp message';
    return entry.after?.status === 'failed' ? `${what} — not delivered` : what;
  }
  const before = { status: 'new', organization: null, tags: [], follow_up_at: null, ...(entry.before || {}) };
  const after = entry.after || {};
  const changes = Object.keys(FIELDS).filter((key) => key in after
    && JSON.stringify(before[key] ?? null) !== JSON.stringify(after[key] ?? null));
  return changes.map((key) => `${FIELDS[key]}: ${shown(key, before[key])} → ${shown(key, after[key])}`).join(' · ')
    || 'Saved the follow-up (no change)';
}

function Feedback({ state }) {
  if (!state.message) return null;
  return <p className={`adm-feedback${state.error ? ' adm-error' : ''}`} role={state.error ? 'alert' : 'status'}>{state.message}</p>;
}

function PlanForm({ user, onSaved }) {
  const [plan, setPlan] = useState(user.plan);
  const [state, setState] = useState({});
  useEffect(() => { setPlan(user.plan); }, [user.plan]);
  const submit = async (event) => {
    event.preventDefault();
    if (plan === user.plan || state.busy) return;
    setState({ busy: true });
    try {
      await api.put(`/users/${encodeURIComponent(user.id)}/plan`, { plan });
      setState({ message: `Plan changed to ${plan}.` });
      onSaved();
    } catch (error) { setState({ message: errorText(error), error: true }); }
  };
  return <form className="adm-form" onSubmit={submit}>
    <div className="adm-form-row">
      <label className="ws-field">Plan
        <select value={plan} onChange={(event) => { setPlan(event.target.value); setState({}); }} disabled={state.busy}>
          {PLANS.concat(PLANS.includes(user.plan) ? [] : [user.plan]).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </label>
      <button type="submit" className="ws-primary" disabled={plan === user.plan || state.busy}>
        {plan === user.plan ? 'Current plan' : `Change to ${plan}`}
      </button>
    </div>
    <p className="adm-hint">
      {user.plan_source === 'manual' ? `Set by the team on ${day(user.plan_changed_at)}.` : 'Plan from sign-up or payment.'}
      {user.stripe && ' This user has a Stripe customer: a Stripe payment event can change the plan again.'}
    </p>
    <Feedback state={state} />
  </form>;
}

const followUpOf = (user) => ({
  status: user.crm.status, organization: user.crm.organization || '',
  tags: user.crm.tags.join(', '), follow_up_at: user.crm.follow_up_at || '',
});

function FollowUpForm({ user, onSaved }) {
  const [form, setForm] = useState(() => followUpOf(user));
  const [state, setState] = useState({});
  useEffect(() => { setForm(followUpOf(user)); }, [user]);
  const changed = JSON.stringify(form) !== JSON.stringify(followUpOf(user));
  const set = (key) => (event) => { setForm({ ...form, [key]: event.target.value }); setState({}); };
  const submit = async (event) => {
    event.preventDefault();
    if (!changed || state.busy) return;
    setState({ busy: true });
    try {
      await api.put(`/users/${encodeURIComponent(user.id)}/crm`, {
        status: form.status, organization: form.organization.trim(),
        tags: form.tags.split(',').map((tag) => tag.trim()).filter(Boolean), follow_up_at: form.follow_up_at || null,
      });
      setState({ message: 'Follow-up saved.' });
      onSaved();
    } catch (error) { setState({ message: errorText(error), error: true }); }
  };
  return <form className="adm-form" onSubmit={submit}>
    <div className="adm-form-grid">
      <label className="ws-field">Stage
        <select value={form.status} onChange={set('status')}>
          {Object.entries(STAGES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label className="ws-field">Follow up on<input type="date" value={form.follow_up_at} onChange={set('follow_up_at')} /></label>
      <label className="ws-field">Organization<input value={form.organization} onChange={set('organization')} maxLength={120} placeholder="Cooperative, bank, NGO…" /></label>
      <label className="ws-field">Tags<input value={form.tags} onChange={set('tags')} placeholder="pilot, Rwanda, maize" /></label>
    </div>
    <div className="adm-form-row"><button type="submit" className="ws-primary" disabled={!changed || state.busy}>Save follow-up</button><Feedback state={state} /></div>
  </form>;
}

const CHANNEL = { web: 'Web', whatsapp: 'WhatsApp' };
const fill = (body, params, values) => body.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g,
  (whole, name) => values[params.indexOf(name)] || whole);

// Write to the user: email from info@upyachat.com, or WhatsApp from the bot's number (free text within
// 24 hours of their last message, otherwise an approved template). The server decides who may receive.
function WriteTo({ user, sending, onSent }) {
  const canEmail = Boolean(user.email);
  const canWhatsApp = Boolean(user.whatsapp) && sending?.whatsapp !== false;
  const [channel, setChannel] = useState(canEmail ? 'email' : 'whatsapp');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [templates, setTemplates] = useState(null);
  const [choice, setChoice] = useState('');
  const [values, setValues] = useState([]);
  const [state, setState] = useState({});
  const windowOpen = Boolean(user.whatsapp_window?.open);
  const useTemplate = channel === 'whatsapp' && !windowOpen;
  const firstName = (user.name || '').split(' ')[0];
  useEffect(() => {
    if (!useTemplate || templates || sending?.mode === 'off') return;
    api.get('/whatsapp/templates').then(setTemplates)
      .catch(() => setTemplates({ configured: true, templates: [], error: 'Could not load the templates.' }));
  }, [useTemplate, templates, sending]);
  const template = templates?.templates?.find((t) => `${t.name}|${t.language}` === choice);
  const pick = (value) => {
    setChoice(value); setState({});
    const t = templates?.templates?.find((x) => `${x.name}|${x.language}` === value);
    setValues(t ? t.params.map((_, i) => (i === 0 ? firstName : '')) : []);
  };
  if (!canEmail && !canWhatsApp) return <p className="adm-muted">No email address and no WhatsApp number on this account.</p>;
  const off = sending?.mode === 'off';
  const ready = channel === 'email' ? subject.trim() && text.trim()
    : useTemplate ? template && values.every((v) => v.trim()) : text.trim();
  const send = async (event) => {
    event.preventDefault();
    if (!ready || state.busy || off) return;
    setState({ busy: true });
    try {
      const path = `/users/${encodeURIComponent(user.id)}/${channel}`;
      await api.post(path, channel === 'email' ? { subject: subject.trim(), body: text.trim() }
        : useTemplate ? { template: { name: template.name, language: template.language, params: values.map((v) => v.trim()) } }
          : { text: text.trim() });
      setState({ message: channel === 'email' ? `Email sent to ${user.email}.` : 'WhatsApp message sent.' });
      setSubject(''); setText(''); setChoice(''); setValues([]);
      onSent();
    } catch (err) {
      setState({ message: errorText(err), error: true });
      if (err?.response?.status === 502) onSent();      // the failed attempt is kept in the list
    }
  };
  return <form className="adm-form adm-write" onSubmit={send}>
    {off && <p className="adm-notice adm-notice-inline" role="note">Sending is turned off on this server.</p>}
    {sending?.mode === 'test' && <p className="adm-notice adm-notice-inline" role="note">Test mode: this server only sends to its test recipients.</p>}
    <div className="adm-channel" role="group" aria-label="Channel">
      <button type="button" aria-pressed={channel === 'email'} disabled={!canEmail} onClick={() => { setChannel('email'); setState({}); }}><FiMail aria-hidden="true" />Email</button>
      <button type="button" aria-pressed={channel === 'whatsapp'} disabled={!canWhatsApp} onClick={() => { setChannel('whatsapp'); setState({}); }}><FaWhatsapp aria-hidden="true" />WhatsApp</button>
    </div>
    {channel === 'email' ? <>
      <p className="adm-hint">From UpyaChat &lt;info@upyachat.com&gt; to {user.email}. Replies come back to info@upyachat.com.
        A personal message: it reaches them even if they unsubscribed from news.</p>
      <label className="ws-field">Subject<input value={subject} maxLength={200} onChange={(e) => { setSubject(e.target.value); setState({}); }} /></label>
      <label className="ws-field">Message<textarea rows={6} value={text} maxLength={20000} onChange={(e) => { setText(e.target.value); setState({}); }} /></label>
    </> : <>
      <p className="adm-hint">{user.whatsapp_window?.last_inbound
        ? `${firstName || 'They'} last wrote to the bot ${ago(user.whatsapp_window.last_inbound)}.` : `${firstName || 'This person'} has not written to the bot recently.`}{' '}
        {windowOpen ? `You can write freely until ${new Date(user.whatsapp_window.closes_at).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}.`
          : 'Outside the 24-hour window WhatsApp only allows an approved template.'}</p>
      {!useTemplate ? <label className="ws-field">Message<textarea rows={5} value={text} maxLength={4096} onChange={(e) => { setText(e.target.value); setState({}); }} /></label>
        : !templates ? <p className="adm-muted" role="status">{off ? '' : 'Loading the templates…'}</p>
          : !templates.configured ? <p className="adm-hint">No templates yet: create one in WhatsApp Manager, get it approved by Meta, and set WHATSAPP_BUSINESS_ACCOUNT_ID on the server.</p>
            : templates.templates.length === 0 ? <p className="adm-hint">{templates.error || 'No approved template that can be sent from here.'}</p> : <>
              <label className="ws-field">Template
                <select value={choice} onChange={(e) => pick(e.target.value)}>
                  <option value="">Choose…</option>
                  {templates.templates.map((t) => <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} · {t.language}{t.category ? ` · ${t.category.toLowerCase()}` : ''}</option>)}
                </select>
              </label>
              {template && template.params.map((name, i) => <label key={name} className="ws-field">{`Variable {{${name}}}`}
                <input value={values[i] || ''} maxLength={500} onChange={(e) => { const next = [...values]; next[i] = e.target.value; setValues(next); setState({}); }} />
              </label>)}
              {template && <p className="adm-preview" aria-label="Preview">{fill(template.body, template.params, values)}</p>}
            </>}
    </>}
    <div className="adm-form-row">
      <button type="submit" className="ws-primary" disabled={!ready || state.busy || off}>{state.busy ? 'Sending…' : channel === 'email' ? 'Send email' : useTemplate ? 'Send template' : 'Send WhatsApp message'}</button>
      <Feedback state={state} />
    </div>
  </form>;
}

// News emails can be refused; the team records it when the person asks by phone, WhatsApp or reply.
function NewsSubscription({ user, onSaved }) {
  const [state, setState] = useState({});
  const subscribed = !user.unsubscribed_at;
  const change = async () => {
    setState({ busy: true });
    try {
      await api.put(`/users/${encodeURIComponent(user.id)}/subscription`, { news: !subscribed });
      setState({});
      onSaved();
    } catch (err) { setState({ message: errorText(err), error: true }); }
  };
  return <div className="adm-news">
    <span>{subscribed ? 'Receives news emails.'
      : `Unsubscribed from news emails on ${day(user.unsubscribed_at)}. Official and personal emails still reach them.`}</span>
    <button type="button" className="adm-link" onClick={change} disabled={state.busy}>
      {subscribed ? 'Unsubscribe them (they asked)' : 'Subscribe them again (they asked)'}</button>
    <Feedback state={state} />
  </div>;
}

function SentMessages({ messages }) {
  if (!messages?.length) return null;
  return <ul className="adm-timeline adm-sent">{messages.map((m) => <li key={m.id}>
    <p>{m.channel === 'email' ? <FiMail aria-hidden="true" /> : <FaWhatsapp aria-hidden="true" />}{' '}
      <strong>{m.subject || 'WhatsApp message'}</strong>{' '}
      {m.status === 'sent' ? <span className="adm-ok"><FiCheck aria-hidden="true" /> sent</span>
        : <span className="adm-error"><FiAlertCircle aria-hidden="true" /> not delivered{m.error ? `: ${m.error}` : ''}</span>}</p>
    <p className="adm-sent-body">{m.body}</p>
    <small>{m.sent_by} · {day(m.created_at)} · to {m.recipient}</small>
  </li>)}</ul>;
}

// One conversation, read-only. The server records every opening in the user's history.
function Transcript({ user, conversationId, onBack, onOpened }) {
  const [talk, setTalk] = useState(null);
  const [error, setError] = useState('');
  const onOpenedRef = useRef(onOpened);
  onOpenedRef.current = onOpened;
  useEffect(() => {
    let active = true;
    api.get(`/users/${encodeURIComponent(user.id)}/conversations/${encodeURIComponent(conversationId)}`)
      .then((data) => { if (active) { setTalk(data); onOpenedRef.current?.(); } })
      .catch((err) => { if (active) setError(errorText(err)); });
    return () => { active = false; };
  }, [user.id, conversationId]);
  return <div className="adm-transcript">
    <button type="button" className="adm-back" onClick={onBack}><FiArrowLeft aria-hidden="true" />All conversations</button>
    {error ? <p className="adm-feedback adm-error" role="alert">{error}</p> : !talk ? <p className="adm-muted" role="status">Loading…</p> : <>
      <p className="adm-hint">{CHANNEL[talk.source] || talk.source} · {day(talk.created_at)}{talk.updated_at.slice(0, 10) !== talk.created_at.slice(0, 10) ? ` → ${day(talk.updated_at)}` : ''}</p>
      <ol className="adm-messages">
        {talk.messages.map((m, i) => <li key={i} className={m.from === 'user' ? 'adm-message-user' : 'adm-message-bot'}>
          <small>{m.from === 'user' ? identity(user).title : 'UpyaChat'}{m.agent && m.from !== 'user' ? ` · ${m.agent}` : ''}</small>
          {m.image && <span className="adm-badge adm-badge-soft"><FiImage aria-hidden="true" /> photo</span>}
          {m.from === 'user'
            ? <p>{m.content}</p>
            // Answers are Markdown; rendered the way the chat renders them (sanitised by DOMPurify).
            : <div className="adm-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} />}
        </li>)}
      </ol>
      {talk.messages.length === 0 && <p className="adm-muted">This conversation has no readable messages.</p>}
    </>}
  </div>;
}

function NoteForm({ user, onSaved }) {
  const [body, setBody] = useState('');
  const [state, setState] = useState({});
  const submit = async (event) => {
    event.preventDefault();
    if (!body.trim() || state.busy) return;
    setState({ busy: true });
    try {
      await api.post(`/users/${encodeURIComponent(user.id)}/notes`, { body: body.trim() });
      setBody(''); setState({});
      onSaved();
    } catch (error) { setState({ message: errorText(error), error: true }); }
  };
  return <form className="adm-form" onSubmit={submit}>
    <label className="ws-field">New note
      <textarea rows={3} value={body} maxLength={4000} onChange={(event) => setBody(event.target.value)} placeholder="Call, email, meeting: what was said and what comes next." />
    </label>
    <div className="adm-form-row"><button type="submit" className="ws-secondary" disabled={!body.trim() || state.busy}>Add note</button><Feedback state={state} /></div>
  </form>;
}

export default function AdminUserPanel({ userId, sending, onClose, onChanged }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [reading, setReading] = useState(null);   // id of the conversation being read
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    let active = true;
    setError('');
    api.get(`/users/${encodeURIComponent(userId)}`)
      .then((data) => { if (active) setUser(data); })
      .catch((err) => { if (active) setError(err?.response?.status === 404 ? 'This user no longer exists.' : errorText(err)); });
    return () => { active = false; };
  }, [userId, reload]);
  useEffect(() => {   // once: focus moves into the panel, Escape closes it
    closeRef.current?.focus();
    const onKey = (event) => { if (event.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const saved = () => { setReload((n) => n + 1); onChanged?.(); };
  const months = user ? lastMonths(user.activity_by_month || []) : [];
  const phone = user?.whatsapp ? user.whatsapp.replace(/\D/g, '') : '';

  return <div className="adm-panel-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="adm-panel" role="dialog" aria-modal="true" aria-labelledby="adm-panel-title">
      <header className="adm-panel-header">
        <div>
          <h2 id="adm-panel-title">{user ? identity(user).title : 'User'}</h2>
          {user && identity(user).subtitle && <p>{identity(user).subtitle}</p>}
        </div>
        <button ref={closeRef} type="button" className="ws-icon-button" onClick={onClose} aria-label="Close"><FiX /></button>
      </header>
      {error ? <p className="adm-feedback adm-error" role="alert">{error}</p> : !user ? <p className="adm-muted" role="status">Loading…</p> : <>
        <div className="adm-badges">
          <span className="adm-badge">{user.plan}</span>
          <span className="adm-badge adm-badge-soft">{STAGES[user.crm.status] || user.crm.status}</span>
          {!user.verified && !user.whatsapp_only && <span className="adm-badge adm-badge-warn">Email not verified</span>}
          {user.suspended && <span className="adm-badge adm-badge-warn">Suspended</span>}
          {user.unsubscribed_at && <span className="adm-badge adm-badge-soft" title={day(user.unsubscribed_at)}>Unsubscribed from news</span>}
        </div>
        {phone && <p className="adm-hint"><FaWhatsapp aria-hidden="true" /> <a href={`https://wa.me/${phone}`} target="_blank" rel="noreferrer">{user.whatsapp}</a></p>}
        <dl className="adm-facts">
          <div><dt>Signed up</dt><dd>{day(user.created_at)}</dd></div>
          <div><dt>Last question</dt><dd title={user.last_active ? day(user.last_active) : undefined}>{ago(user.last_active) || 'never'}</dd></div>
          <div><dt>Last opened the app</dt><dd>{user.last_opened ? day(user.last_opened) : '–'}</dd></div>
          <div><dt>Conversations</dt><dd>{number(user.conversations)}</dd></div>
          <div><dt>Questions</dt><dd>{number(user.questions)}{user.questions_whatsapp > 0 && <small> ({number(user.questions_whatsapp)} on WhatsApp)</small>}</dd></div>
          <div><dt>Projects</dt><dd>{number(user.projects)}</dd></div>
          <div><dt>Channels</dt><dd>{user.channels.map((c) => CHANNEL[c] || c).join(' + ') || '–'}</dd></div>
          <div><dt>Language</dt><dd>{user.language || '–'}</dd></div>
        </dl>
        <Bars label="Questions per month" rows={months} keyOf={(row) => row.month} value={(row) => row.questions}
          summary={`${number(months.reduce((sum, row) => sum + row.questions, 0))} in 12 months`}
          describe={(row) => `${row.month}: ${row.questions} questions in ${row.conversations} conversations`} />
        <section className="adm-panel-section" aria-labelledby="adm-write"><h3 id="adm-write">Write to {identity(user).title}</h3>
          {user.email && <NewsSubscription user={user} onSaved={() => setReload((n) => n + 1)} />}
          <WriteTo key={user.id} user={user} sending={sending} onSent={() => setReload((n) => n + 1)} />
          <SentMessages messages={user.messages} />
        </section>
        <section className="adm-panel-section" aria-labelledby="adm-conversations"><h3 id="adm-conversations">Conversations</h3>
          {reading
            ? <Transcript user={user} conversationId={reading} onBack={() => setReading(null)} onOpened={() => setReload((n) => n + 1)} />
            : user.recent_conversations.length === 0 ? <p className="adm-muted">No conversation yet.</p> : <>
              <p className="adm-hint">Opening a conversation is recorded in the history below.</p>
              <ul className="adm-conversations">
                {user.recent_conversations.map((c) => <li key={c.id}><button type="button" onClick={() => setReading(c.id)}>
                  <span className="adm-conversation-title">{c.title || 'Untitled'}</span>
                  <small>{day(c.updated_at)} · {CHANNEL[c.source] || c.source} · {number(c.questions)} {c.questions === 1 ? 'question' : 'questions'}</small>
                </button></li>)}
              </ul>
            </>}
        </section>
        <section className="adm-panel-section" aria-labelledby="adm-plan"><h3 id="adm-plan">Plan</h3><PlanForm user={user} onSaved={saved} /></section>
        <section className="adm-panel-section" aria-labelledby="adm-follow"><h3 id="adm-follow">Follow-up</h3><FollowUpForm user={user} onSaved={saved} /></section>
        <section className="adm-panel-section" aria-labelledby="adm-notes"><h3 id="adm-notes">Notes</h3>
          <NoteForm user={user} onSaved={saved} />
          <ul className="adm-timeline">{user.notes.map((note) => <li key={note.id}><p>{note.body}</p><small>{note.author_email} · {day(note.created_at)}</small></li>)}</ul>
        </section>
        {user.audit.length > 0 && <section className="adm-panel-section" aria-labelledby="adm-history"><h3 id="adm-history">History</h3>
          <ul className="adm-timeline">{user.audit.map((entry, i) => <li key={`${entry.created_at}-${i}`}><p>{describeAudit(entry)}</p><small>{entry.actor_email} · {day(entry.created_at)}</small></li>)}</ul>
        </section>}
      </>}
    </aside>
  </div>;
}
